import { useState, useEffect } from "react";
import { deferredInstallPrompt } from "../frontend";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPrompt = () => {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(
    deferredInstallPrompt as BeforeInstallPromptEvent | null
  );
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome|crios|fxios/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setShowIOSPrompt(true);
      return;
    }

    // Pick up the event if it fires after React mounts
    const handler = () => {
      setInstallEvent(deferredInstallPrompt as BeforeInstallPromptEvent);
    };
    window.addEventListener("pwa-install-ready", handler);
    return () => window.removeEventListener("pwa-install-ready", handler);
  }, []);

  const handleInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstallEvent(null);
  };

  if (dismissed || (!installEvent && !showIOSPrompt)) return null;

  return (
    <div className='fixed bottom-4 left-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex items-center justify-between max-w-md mx-auto z-50'>
      <div>
        <p className='text-sm font-semibold text-gray-900'>Install app</p>
        {showIOSPrompt ? (
          <p className='text-xs text-gray-500'>
            Tap <strong>Share</strong> then <strong>Add to Home Screen</strong>
          </p>
        ) : (
          <p className='text-xs text-gray-500'>Add to your home screen for quick access</p>
        )}
      </div>
      <div className='flex gap-2 ml-4 flex-shrink-0'>
        {!showIOSPrompt && (
          <button
            onClick={handleInstall}
            className='px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700'>
            Install
          </button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className='px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-md hover:bg-gray-200'>
          {showIOSPrompt ? "OK" : "Not now"}
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
